import AntInput from 'antd/lib/input'
import React from 'react'
import styled, { css } from 'styled-components'

import { color } from '../styles'
import { SPACING, BORDER_RADIUS } from '../styles/mixins/constants'
import { text } from '../styles/mixins/text'
import { isKeyPressEnter } from '../util/isKeyPressEnter'
import { insertTestingSelector, TestingKey } from '../util/testing'

import { IconKey } from './Icon/index'
import { InputContainer, LabelPosition } from './InputContainer'

export type NaturalTextInputEvent = (keyboardEvent: React.ChangeEvent<HTMLInputElement>) => void
export interface TextInputProps {
    addonBefore?: JSX.Element | string
    addonAfter?: JSX.Element | string
    errorMessage?: string
    disabled?: boolean
    disableClear?: boolean
    className?: string
    value?: string | number | null
    type?: 'text' | 'number' | 'tel' | 'password' | 'email' | 'url'
    label?: string
    testingKeys?: [TestingKey] | [TestingKey, string]
    placeholder?: string
    leftIconKey?: IconKey
    labelPosition?: LabelPosition
    disableBorder?: boolean
    onChange?: (value: string) => void
    onEventChange?: NaturalTextInputEvent
    onBlur?: () => void
    hasDanger?: boolean
    name?: string
    onEnterPress?: () => void
}

export const TextInput = (props: TextInputProps): JSX.Element => {
    const {
        value,
        className,
        label,
        testingKeys,
        disabled,
        errorMessage,
        placeholder,
        name,
        hasDanger: displayDanger,
        disableClear,
        addonBefore,
        addonAfter,
        type = 'text',
        labelPosition = 'vertical',
        onEnterPress,
        onChange,
        onBlur,
        onEventChange,
    } = props

    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (!isKeyPressEnter(e)) return
        if (!onEnterPress) return
        onEnterPress()
    }

    const testingKey = testingKeys?.[0]
    const hasErrorState = Boolean(errorMessage || displayDanger)
    const testingSelectors = testingKeys ? insertTestingSelector(...testingKeys) : {}
    return (
        <Container
            label={label}
            labelPosition={labelPosition}
            disabled={Boolean(disabled)}
            errorMessage={errorMessage}
            hasDanger={hasErrorState}
            className={className}
            testingKey={testingKey}
        >
            <AntInput
                {...testingSelectors}
                addonBefore={addonBefore}
                addonAfter={addonAfter}
                name={name}
                disabled={disabled}
                value={value || undefined}
                placeholder={placeholder}
                allowClear={!disableClear}
                type={type}
                onChange={(e) => {
                    if (onEventChange) {
                        return onEventChange(e)
                    }
                    return onChange?.(e.target.value)
                }}
                onBlur={onBlur}
                onKeyUp={handleKeyUp}
            />
        </Container>
    )
}

const BASE_STYLE = css`
    padding: ${SPACING[1]} ${SPACING[3]};
    border-radius: ${BORDER_RADIUS};
    border: 1px solid ${color.border.muted};

    input::placeholder {
        ${text('paragraph', 'muted', 'text')}
    }

    input {
        ${text('paragraph', 'primary', 'text')}
        background-color: ${color.background.content};
    }

    & .ant-input-clear-icon {
        color: ${color.icon.secondary};
    }
    &:hover {
        border: 1px solid ${color.brand.primary};
    }
`

const FOCUS_STYLE = css`
    border: 1px solid ${color.brand.primary};
    z-index: 400;
`

const DISABLED_STYLE = css`
    border: 1px solid ${color.border.primary};
    background-color: ${color.background.muted};
    user-select: none;
    cursor: not-allowed;
    &:hover {
        border: 1px solid ${color.border.primary};
        background-color: ${color.background.muted};
        user-select: none;
        cursor: not-allowed;
    }
`

const ERROR_STYLE = css`
    &,
    &:hover,
    &:focus,
    &:active,
    .ant-input-affix-wrapper-focused {
        border: 1px solid ${color.status.danger};
        box-shadow: 0px 0px 0px 0px;
        z-index: 400;
    }
`

const Container = styled(InputContainer)`
    & .ant-input-affix-wrapper {
        ${BASE_STYLE}
    }

    & .ant-input-affix-wrapper-focused {
        ${FOCUS_STYLE}
    }

    .ant-input-affix-wrapper-disabled {
        border: 1px solid ${color.border.primary};
        background-color: ${color.background.muted};
        user-select: none;
        cursor: not-allowed;
        &:hover {
            border: 1px solid ${color.border.primary};
        }
    }
    ${(p) => {
        if (p.disabled) return ''
        if (!p.hasDanger && !p.errorMessage) return ''
        return css`
            .ant-input-affix-wrapper {
                ${ERROR_STYLE}
            }
        `
    }}
    .ant-input-group-addon {
        padding: 0px;
    }
`

export const TextInputElements = {
    DISABLED_STYLE,
    ERROR_STYLE,
    BASE_STYLE,
    FOCUS_STYLE,
}
